<script setup>
// @ts-check

import { dateToString, stringToDate } from '@/helpers/dates'
import { format, isDate, isWeekend } from 'date-fns'
import finnishholidays from 'finnish-holidays-js'
import { computed, useSlots } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  disablePublicHolidays: {
    type: Boolean
  },
  disableWeekends: {
    type: Boolean
  }
})

const model = defineModel({
  /** @type {import('vue').PropType<string|Date|null>} */
  type: [String, Date],
  default: null
})

defineOptions({
  name: 'DateField'
})

const { t } = useI18n()

/** @type {import('vuetify/labs/VDateInput').VDateInput['$slots']} */
const slots = useSlots()

/** @param {?(Date|string)} selectedDate */
const onDateUpdate = (selectedDate) => {
  if (!selectedDate) {
    model.value = null
    return
  }

  if (typeof selectedDate === 'string') {
    model.value = selectedDate
    return
  }

  model.value = dateToString(selectedDate, 'yyyy-MM-dd')
}

const dateObject = computed(() => {
  if (!model.value) {
    return null
  }

  if (typeof model.value === 'string') {
    return stringToDate(model.value)
  }

  return model.value
})

const publicHolidays = (year = new Date().getFullYear()) =>
  finnishholidays.year(year).map((holiday) => {
    return format(
      new Date(`${holiday.year}/${holiday.month}/${holiday.day}`),
      'dd.MM.yyyy'
    )
  })

const allowedDates = (/** @type {unknown} */ date) => {
  if (!isDate(date)) {
    return true
  }

  if (!props.disableWeekends && !props.disablePublicHolidays) {
    return true
  }

  if (props.disableWeekends && isWeekend(date)) {
    return false
  }

  if (props.disablePublicHolidays) {
    const yearShown = date.getFullYear()
    const publicHolidaysArr = publicHolidays(yearShown)

    if (publicHolidaysArr.includes(format(date, 'dd.MM.yyyy'))) {
      return false
    }
  }

  return true
}
</script>

<template>
  <VDateInput
    :model-value="dateObject"
    :placeholder="t('dates.placeholder')"
    :allowed-dates
    @update:model-value="onDateUpdate"
  >
    <template v-for="(slot, index) in Object.keys(slots)" :key="index" #[slot]>
      <slot :name="slot" />
    </template>
  </VDateInput>
</template>
